import React, { useEffect, useState } from 'react';
import { Modal, PillButton, Row, Stack, Text } from 'library/ui';
import useLoading from 'library/hooks/useLoading';
import { format12HourTime, formatMonthDay } from 'shared/Dates';
import { IShift } from 'models';
import moment from 'moment';
import { DayOfWeekSelections, ProviderSelection } from 'parent-portal/forms/JobRequestTypes';
import PreferredWorkersInput from 'parent-portal/forms/components/pages/ProviderPreferencesPage/PreferredWorkersInput';
import PastWorkersInput from 'parent-portal/forms/components/pages/ProviderPreferencesPage/PastWorkersInput';
import { updateBusinessAppointment } from './businessAppointmentApi';
import { convertPreferredSitterObject } from 'parent-portal/forms/components/pages/ProviderPreferencesPage/preferredWorkersApi';
import { JobFormContextProvider, useJobFormContext } from 'parent-portal/forms/JobFormContext';

function UpdatePreferredWorkerModalContent({
    onClose,
    shift,
    refresh,
}: {
    onClose: () => void;
    shift: IShift;
    refresh: () => void;
}) {
    const matchedWorkerSelections = shift.matches?.map((match) => ({
        id: match.matched_worker.id,
        name: `${match.matched_worker.first_name} ${match.matched_worker.last_name}`,
    }));

    const [preferredWorkers, setPreferredWorkers] = useState<ProviderSelection[]>([]);
    const [pastWorkers, setPastWorkers] = useState<ProviderSelection[]>([]);
    const { form } = useJobFormContext();

    useEffect(() => {
        const start = moment(shift.start_date);
        const end = moment(shift.end_date);
        const dayName = start.format('dddd') as keyof DayOfWeekSelections;
        const days = form.fieldStates.daysOfTheWeek.value;

        form.fieldStates.address.setValue([{ id: shift.ongoing_request.address.id }]);
        form.fieldStates.startDate.setValue(start);
        form.fieldStates.endDate.setValue(end);
        form.fieldStates.daysOfTheWeek.setValue({
            ...days,
            [dayName]: { ...days[dayName], selected: true, start, end },
        });
    }, []);

    const { loading, setLoading } = useLoading();
    function submit() {
        setLoading(true);
        updateBusinessAppointment(shift.id, {
            preferred_sitters: convertPreferredSitterObject(preferredWorkers, pastWorkers),
        })
            .then(() => {
                refresh();
                onClose();
            })
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <Stack gap="md">
            <Text size="md">
                {shift.ongoing_request.headline}, {formatMonthDay(shift.start_date)}{' '}
                {format12HourTime(shift.start_date)}-{format12HourTime(shift.end_date)} (
                {shift.ongoing_request.business_job_type.replace('_', ' ').toLowerCase().capitalize()})
            </Text>
            <PreferredWorkersInput
                selected={preferredWorkers}
                onChange={setPreferredWorkers}
                existingMatches={matchedWorkerSelections}
            />
            <PastWorkersInput selected={pastWorkers} onChange={setPastWorkers} />
            <Row justify="end" fullWidth>
                <PillButton disabled={loading} onClick={submit}>
                    {loading ? 'Submitting…' : 'Submit'}
                </PillButton>
            </Row>
        </Stack>
    );
}

export default function UpdatePreferredWorkerModal({
    open,
    onClose,
    shift,
    refresh,
}: {
    open: boolean;
    onClose: () => void;
    shift: IShift;
    refresh: () => void;
}) {
    if (!open) {
        return null;
    }

    return (
        <Modal title="Request Specific Worker for Shift" onClose={onClose}>
            <JobFormContextProvider>
                <UpdatePreferredWorkerModalContent onClose={onClose} shift={shift} refresh={refresh} />
            </JobFormContextProvider>
        </Modal>
    );
}
